import React, { useState } from "react";
import { TspFormField } from "../../../../components/primitives";
import { EMPTY_ACCOMMODATION, validateAccommodation } from "../../addJourneyCityDetails";
import SectionShell, { FormButtons, SectionForm } from "./SectionShell";

const FIELDS = [
  { key: "stay_name", label: "Stay Name", required: true, placeholder: "e.g. ISKCON Guest House" },
  { key: "contact", label: "Contact", placeholder: "Contact person / phone" },
  { key: "address", label: "Address", placeholder: "Full address" },
  { key: "charges", label: "Charges (₹)", placeholder: "Leave empty if free", inputMode: "decimal" },
  { key: "capacity", label: "Capacity", placeholder: "Devotees", inputMode: "numeric" },
  { key: "remarks", label: "Remarks", placeholder: "Optional" },
];

const text = (value) => (value === null || value === undefined ? "" : String(value));

// Accommodation: where the team stays at this place (any number of stays) —
// the same records the Accommodation screen keeps, for this place and journey.
// store.stays = [{ id, stay_name, contact, address, charges, capacity, remarks }].
export default function AccommodationSection({ idPrefix, store, canManage, disabled }) {
  return (
    <SectionShell
      idPrefix={idPrefix}
      title="Accommodation"
      addLabel="Add Accommodation"
      emptyText="No accommodation recorded for this place yet."
      items={store.stays}
      loading={store.staysLoading}
      error={store.staysError}
      onRetry={store.reloadStays}
      canAdd={canManage}
      canEdit={() => canManage}
      canRemove={() => canManage}
      onAdd={store.addStay}
      onUpdate={store.updateStay}
      onRemove={store.removeStay}
      confirmRemove="Remove this accommodation?"
      removeLabel={(stay) => `Remove ${stay.stay_name}`}
      disabled={disabled}
      renderItem={(stay) => (
        <dl className="tsp-pc-facts">
          <div>
            <dt>Stay</dt>
            <dd>
              <strong>{stay.stay_name}</strong>
            </dd>
          </div>
          <div>
            <dt>Contact</dt>
            <dd>{text(stay.contact) || "—"}</dd>
          </div>
          <div>
            <dt>Charges</dt>
            <dd>{text(stay.charges).trim() ? `₹${stay.charges}` : "Free"}</dd>
          </div>
          <div>
            <dt>Capacity</dt>
            <dd>{text(stay.capacity).trim() ? `${stay.capacity} devotees` : "—"}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>{text(stay.address) || "—"}</dd>
          </div>
          <div>
            <dt>Remarks</dt>
            <dd>{text(stay.remarks) || "—"}</dd>
          </div>
        </dl>
      )}
      renderForm={(form) => <StayForm {...form} />}
    />
  );
}

function StayForm({ initial, submit, cancel, busy, idPrefix }) {
  const [draft, setDraft] = useState(() => (initial ? Object.fromEntries(FIELDS.map((field) => [field.key, text(initial[field.key])])) : { ...EMPTY_ACCOMMODATION }));
  const [errors, setErrors] = useState({});
  const id = (field) => `${idPrefix}-${field}`;

  const onSubmit = () => {
    const found = validateAccommodation(draft);
    setErrors(found);
    const first = FIELDS.find((field) => found[field.key]);
    if (first) {
      document.getElementById(id(first.key))?.focus();
      return;
    }
    submit(draft);
  };

  return (
    <SectionForm onSubmit={onSubmit}>
      <div className="tsp-aj-grid">
        {FIELDS.map((field) => (
          <TspFormField key={field.key} id={id(field.key)} label={field.label} required={field.required} error={errors[field.key]}>
            <input
              id={id(field.key)}
              type="text"
              inputMode={field.inputMode}
              className="tsp-input"
              placeholder={field.placeholder}
              value={draft[field.key]}
              disabled={busy}
              aria-invalid={errors[field.key] ? "true" : undefined}
              onChange={(event) => {
                setDraft({ ...draft, [field.key]: event.target.value });
                setErrors({ ...errors, [field.key]: undefined });
              }}
            />
          </TspFormField>
        ))}
      </div>
      <FormButtons busy={busy} onSave={onSubmit} onCancel={cancel} saveLabel="Save Accommodation" />
    </SectionForm>
  );
}
